<template>
  <div class="dash">
    <!-- ═══ Encabezado ═══ -->
    <div class="header-flex">
      <div>
        <h1>{{ saludo }}, {{ nombre }} <span class="rol-chip">{{ rol }}</span></h1>
        <p class="subtitulo">{{ hoyLargo }}</p>
      </div>
    </div>
    <div class="divider"></div>

    <!-- ═══ Skeleton mientras carga ═══ -->
    <template v-if="cargando">
      <div class="kpis"><div v-for="i in 4" :key="i" class="skeleton sk-kpi"></div></div>
      <div class="grid">
        <div class="skeleton sk-tarjeta col-7"></div>
        <div class="skeleton sk-tarjeta col-5"></div>
      </div>
    </template>

    <div v-else-if="error" class="aviso-error">
      <button class="btn chico" @click="cargar">Reintentar</button>
    </div>

    <template v-else-if="d">
      <!-- ═══ Fila de 4 KPIs (regla de los 5 segundos) ═══ -->
      <section class="kpis">
        <button class="kpi" :class="kpiUrgencia" @click="ir('/calendario')">
          <template v-if="d.kpis.proximo_evento">
            <span class="kpi-num">{{ d.kpis.proximo_evento.dias_restantes }}<small> días</small></span>
            <span class="kpi-lbl">{{ d.kpis.proximo_evento.titulo }}</span>
            <span class="kpi-sub">{{ d.kpis.proximo_evento.tipo_label }} · {{ fmtFecha(d.kpis.proximo_evento.fecha) }}</span>
          </template>
          <template v-else>
            <span class="kpi-num">—</span>
            <span class="kpi-lbl">Sin fechas registradas</span>
            <span class="kpi-sub">{{ puedeGestionarCal ? 'Registra la primera en el calendario' : 'La Dirección de Calidad las registra' }}</span>
          </template>
        </button>

        <button class="kpi" @click="d.id_ejecucion_foco && ir(`/ejecucion/${d.id_ejecucion_foco}/resultados`)">
          <template v-if="d.kpis.calificacion">
            <span class="kpi-num" :class="'txt-' + d.kpis.calificacion.clasificacion">{{ d.kpis.calificacion.valoracion.toFixed(2) }}</span>
            <span class="kpi-lbl">{{ d.kpis.calificacion.grado ?? 'Calificación global' }}</span>
            <span class="kpi-sub">{{ d.kpis.calificacion.programa }} · {{ d.kpis.calificacion.periodo }}</span>
          </template>
          <template v-else>
            <span class="kpi-num">—</span>
            <span class="kpi-lbl">Sin calificación aún</span>
            <span class="kpi-sub">El roll-up aparece al consolidar indicadores</span>
          </template>
        </button>

        <button class="kpi" @click="d.id_ejecucion_foco && ir(`/ejecucion/${d.id_ejecucion_foco}/avance`)">
          <span class="kpi-num azul">{{ d.kpis.avance.pct === null ? '—' : d.kpis.avance.pct.toFixed(0) + '%' }}</span>
          <span class="kpi-lbl">Avance de evaluación</span>
          <span class="kpi-sub">{{ d.kpis.avance.calificados }} de {{ d.kpis.avance.total }} indicadores calificados</span>
          <span class="kpi-barra"><span class="kpi-barra-rel" :style="{ width: (d.kpis.avance.pct ?? 0) + '%' }"></span></span>
        </button>

        <button class="kpi" :class="{ 'kpi-alerta': d.kpis.alertas_criticas > 0 }" @click="scrollAlertas">
          <span class="kpi-num" :class="d.kpis.alertas_criticas > 0 ? 'txt-rojo' : 'txt-verde'">{{ d.kpis.alertas_criticas }}</span>
          <span class="kpi-lbl">{{ d.kpis.alertas_criticas === 1 ? 'Alerta que requiere acción' : 'Alertas que requieren acción' }}</span>
          <span class="kpi-sub">{{ d.alertas.total }} en total · críticas y altas primero</span>
        </button>
      </section>

      <!-- ═══ Hoy te toca (vista líder) ═══ -->
      <section v-if="d.vista === 'lider' && d.mis_pendientes" class="tarjeta hoy-toca">
        <div class="tarjeta-cab"><h2>Hoy te toca</h2></div>
        <div v-if="!d.mis_pendientes?.factores.length" class="vacio">No tienes factores asignados en procesos activos. ✓</div>
        <div v-else class="toca-grid">
          <button
            v-for="f in d.mis_pendientes?.factores"
            :key="f.id_ejecucion + f.factor"
            class="toca-item"
            @click="ir(`/ejecucion/${f.id_ejecucion}/avance`)"
          >
            <span class="toca-factor">{{ f.factor }}</span>
            <span class="toca-meta">{{ f.programa }} · {{ f.periodo }}</span>
            <span class="toca-datos">
              <span :class="{ 'txt-rojo': f.indicadores_pendientes > 0 }">{{ f.indicadores_pendientes }} indicadores por calificar</span>
              <span v-if="f.bloques_sin_redactar > 0" class="txt-ambar">· {{ f.bloques_sin_redactar }} bloques sin redactar</span>
            </span>
          </button>
        </div>
        <p v-if="d.mis_pendientes?.acciones_por_vencer > 0" class="toca-plan">
          ⚠ {{ d.mis_pendientes?.acciones_por_vencer }} acción(es) del plan de tus características vencen en ≤30 días.
        </p>
      </section>

      <div class="grid">
        <!-- ═══ Centro de alertas ═══ -->
        <section ref="refAlertas" class="tarjeta col-7">
          <div class="tarjeta-cab">
            <h2>Centro de alertas</h2>
            <span class="resumen-sev">
              <span v-if="d.alertas.por_severidad.critica" class="pill p-rojo">{{ d.alertas.por_severidad.critica }} críticas</span>
              <span v-if="d.alertas.por_severidad.alta" class="pill p-ambar">{{ d.alertas.por_severidad.alta }} altas</span>
              <span v-if="d.alertas.por_severidad.media" class="pill p-azul">{{ d.alertas.por_severidad.media }} medias</span>
            </span>
          </div>

          <div v-if="!d.alertas.items.length" class="vacio ok">
            <span class="ok-ico">✓</span> Sin alertas pendientes — todo bajo control.
          </div>
          <ul v-else class="alertas">
            <li v-for="(a, i) in d.alertas.items" :key="a.clave + '-' + i" class="alerta" :class="'b-' + a.severidad">
              <span class="sev-ico" :class="'s-' + a.severidad">{{ a.severidad === 'critica' ? '⚠' : a.severidad === 'alta' ? '!' : 'i' }}</span>
              <div class="alerta-cuerpo" :class="{ enlazable: !!a.enlace_frontend }" @click="a.enlace_frontend && ir(a.enlace_frontend)">
                <span class="alerta-titulo">{{ a.titulo }}</span>
                <span v-if="a.detalle" class="alerta-detalle">{{ a.detalle }}</span>
              </div>
              <button v-if="puedeAtender" class="btn-atender" title="Marcar como atendida" @click="atender(a)">✓</button>
            </li>
          </ul>
          <p v-if="d.alertas.items.length" class="ver-mas">
            {{ d.alertas.items.length }} alerta(s) · las más severas primero<template
              v-if="d.alertas.total > d.alertas.items.length"
            >, mostrando {{ d.alertas.items.length }} de {{ d.alertas.total }}</template>.
          </p>
        </section>

        <!-- ═══ Próximos eventos ═══ -->
        <section class="tarjeta col-5">
          <div class="tarjeta-cab">
            <h2>Próximos eventos</h2>
            <button class="btn chico" @click="ir('/calendario')">Calendario →</button>
          </div>
          <div v-if="!d.proximos_eventos.length" class="vacio">
            Aún no hay eventos próximos<template v-if="puedeGestionarCal"> — <a href="#" @click.prevent="ir('/calendario')">registrar el primero</a></template>.
          </div>
          <ul v-else class="eventos">
            <li v-for="e in d.proximos_eventos" :key="e.id_evento" class="evento" @click="ir('/calendario')">
              <span class="ev-dias" :class="urgencia(e.dias_restantes)">{{ e.dias_restantes }}<small>d</small></span>
              <div class="ev-cuerpo">
                <span class="ev-titulo">{{ e.titulo }}</span>
                <span class="ev-meta">{{ e.tipo_label }}<template v-if="e.programa"> · {{ e.programa }}</template> · {{ fmtFecha(e.fecha_inicio) }}</span>
              </div>
            </li>
          </ul>
        </section>
      </div>

      <!-- ═══ Semáforo de programas (gestión + admin) ═══ -->
      <section v-if="d.vista === 'gestion' || auth.esAdmin" class="tarjeta">
        <div class="tarjeta-cab">
          <h2>Semáforo de programas</h2>
          <span class="cuenta">{{ semaforoFiltrado.length }} proceso(s) activo(s) · ordenados por riesgo</span>
        </div>
        <div v-if="!semaforoFiltrado.length" class="vacio">No hay procesos activos.</div>
        <div v-else class="contenedor-tabla" v-drag-scroll>
          <table class="lista-tabla">
            <thead>
              <tr><th>Programa</th><th>Periodo</th><th>Etapa</th><th class="ctr">Avance</th><th>Hito más próximo</th><th class="ctr">Alertas</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="p in semaforoPagina" :key="p.id_ejecucion" class="fila-prog" @click="ir(`/ejecucion/${p.id_ejecucion}/avance`)">
                <td class="prog-nombre">{{ p.programa }}</td>
                <td>{{ p.periodo }}</td>
                <td><span class="pill" :class="etapaClase(p.estado)">{{ etapaTexto(p.estado) }}</span></td>
                <td class="ctr">
                  <div class="mini-barra"><span :style="{ width: (p.pct_calificados ?? 0) + '%' }"></span></div>
                  <small>{{ p.pct_calificados === null ? 'N/D' : p.pct_calificados.toFixed(0) + '%' }}</small>
                </td>
                <td>
                  <template v-if="p.hito"><b :class="urgenciaTxt(p.hito.dias_restantes)">{{ p.hito.dias_restantes }} días</b> · {{ p.hito.titulo }}</template>
                  <span v-else class="sin-dato">Sin hito registrado</span>
                </td>
                <td class="ctr"><span v-if="p.alertas" class="pill p-rojo">{{ p.alertas }}</span><span v-else class="sin-dato">0</span></td>
                <td class="ctr flecha">→</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="totalPaginasSemaforo > 1" class="semaforo-paginacion">
          <button class="pag-btn" :disabled="paginaSemaforo === 0" @click="paginaSemaforo--">Atrás</button>
          <span class="pag-info">Página {{ paginaSemaforo + 1 }} de {{ totalPaginasSemaforo }}</span>
          <button class="pag-btn" :disabled="paginaSemaforo >= totalPaginasSemaforo - 1" @click="paginaSemaforo++">Siguiente</button>
        </div>
      </section>

      <div v-if="d.vista === 'gestion'" class="grid">
        <!-- ═══ Salud del plan ═══ -->
        <section class="tarjeta col-5">
          <div class="tarjeta-cab"><h2>Plan de mejoramiento</h2></div>
          <div v-if="!d.plan_salud.total" class="vacio">Aún no hay acciones de plan registradas.</div>
          <template v-else>
            <div class="plan-kpis">
              <div class="plan-kpi"><span class="pk-num azul">{{ d.plan_salud.pct_avance?.toFixed(0) ?? '—' }}%</span><span class="pk-lbl">Avance promedio</span></div>
              <div class="plan-kpi"><span class="pk-num" :class="d.plan_salud.vencidas ? 'txt-rojo' : 'txt-verde'">{{ d.plan_salud.vencidas }}</span><span class="pk-lbl">Vencidas</span></div>
              <div class="plan-kpi"><span class="pk-num">{{ d.plan_salud.total }}</span><span class="pk-lbl">Acciones</span></div>
            </div>
            <div class="plan-estados">
              <span v-for="(n, est) in d.plan_salud.por_estado" :key="est" class="pill" :class="estadoPlanClase(String(est))">
                {{ String(est).replace('_', ' ') }}: {{ n }}
              </span>
            </div>
            <button v-if="d.id_ejecucion_foco" class="btn chico enlace-plan" @click="ir(`/ejecucion/${d.id_ejecucion_foco}/plan-mejoramiento`)">Ir al plan →</button>
          </template>
        </section>

        <!-- ═══ Selector avance ═══ -->
        <section class="tarjeta col-7">
          <div class="tarjeta-cab">
            <h2>Avance por factor</h2>
            <label class="selector">Proceso:
              <select v-model.number="ejecucionSeleccionada">
                <option v-for="p in semaforoFiltrado" :key="p.id_ejecucion" :value="p.id_ejecucion">{{ p.programa }} · {{ p.periodo }}</option>
              </select>
            </label>
          </div>
          <AvanceFactorPanel v-if="ejecucionSeleccionada" :id-ejecucion="ejecucionSeleccionada" :key="ejecucionSeleccionada" />
          <div v-else class="vacio">Selecciona un proceso.</div>
        </section>
      </div>

      <!-- ═══ Avance por factor (admin, vista no-gestión) ═══ -->
      <section v-if="d.vista !== 'gestion' && auth.esAdmin" class="tarjeta">
        <div class="tarjeta-cab">
          <h2>Avance por factor</h2>
          <label class="selector">Proceso:
            <select v-model.number="ejecucionSeleccionada">
              <option v-for="p in semaforoFiltrado" :key="p.id_ejecucion" :value="p.id_ejecucion">{{ p.programa }} · {{ p.periodo }}</option>
            </select>
          </label>
        </div>
        <AvanceFactorPanel v-if="ejecucionSeleccionada" :id-ejecucion="ejecucionSeleccionada" :key="ejecucionSeleccionada" />
        <div v-else class="vacio">Selecciona un proceso para ver el avance.</div>
      </section>

      <!-- Avance para vista líder (sus procesos) -->
      <section v-if="d.vista === 'lider' && d.mis_pendientes?.factores.length" class="tarjeta">
        <div class="tarjeta-cab"><h2>Avance de tus procesos</h2></div>
        <AvanceFactorPanel :id-ejecucion="d.mis_pendientes!.factores[0].id_ejecucion" />
      </section>

      <p class="pie">Actualizado {{ horaGenerado }} · los datos se calculan al momento de la consulta.</p>
    </template>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * RF-13 · Tablero principal con alertas y calendario.
 *
 * Las alertas las produce un motor de 13 reglas del backend, y cada una trae la
 * ruta del módulo que la resuelve: se atienden en un clic.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import useAutenticar from '@/store/IngresoAutenticar'
import { useDashboardFiltros } from '@/store/DashboardFiltros'
import { getDashboardService, atenderAlertaService, type DashboardPayload, type AlertaDashboard } from '@/services/DashboardService'
import AvanceFactorPanel from '@/components/AvanceFactorPanel.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'

const router = useRouter()
const auth = useAutenticar()
const filtros = useDashboardFiltros()

const d = ref<DashboardPayload | null>(null)

const mensajeVisible = ref(false)
const mensajeTexto   = ref('')
const mensajeTipo    = ref<'positivo' | 'error'>('error')

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error' = 'error') {
  mensajeTexto.value   = texto
  mensajeTipo.value    = tipo
  mensajeVisible.value = true
}
const cargando = ref(false)
const error = ref('')
const refAlertas = ref<HTMLElement | null>(null)

const SEMAFORO_POR_PAGINA = 10
const paginaSemaforo = ref(0)

const semaforoFiltrado = computed(() =>
  (d.value?.semaforo_programas ?? []).filter(p => p.estado !== 'finalizado')
)
const semaforoPagina = computed(() => {
  const inicio = paginaSemaforo.value * SEMAFORO_POR_PAGINA
  return semaforoFiltrado.value.slice(inicio, inicio + SEMAFORO_POR_PAGINA)
})
const totalPaginasSemaforo = computed(() =>
  Math.ceil(semaforoFiltrado.value.length / SEMAFORO_POR_PAGINA)
)

const nombre = computed(() => d.value?.usuario.name ?? auth.usuarioActual?.name ?? 'Usuario')
const rol = computed(() => d.value?.usuario.rol ?? auth.usuarioActual?.role?.label ?? 'Usuario')
const saludo = computed(() => {
  const h = new Date().getHours()
  return h < 12 ? 'Buenos días' : h < 19 ? 'Buenas tardes' : 'Buenas noches'
})
const hoyLargo = new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

const puedeGestionarCal = computed(() => auth.tienePermiso('calendario.gestionar'))
const puedeAtender = computed(() => auth.tienePermiso('alertas.atender'))

const ejecucionSeleccionada = computed<number | null>({
  get: () => filtros.idEjecucion ?? d.value?.id_ejecucion_foco ?? null,
  set: (v) => { filtros.idEjecucion = v }
})

const kpiUrgencia = computed(() => {
  const dias = d.value?.kpis.proximo_evento?.dias_restantes
  if (dias === undefined || dias === null) return ''
  return dias <= 7 ? 'kpi-critico' : dias <= 30 ? 'kpi-alto' : ''
})

const horaGenerado = computed(() => {
  if (!d.value) return ''
  const t = new Date(d.value.generado_en)
  return isNaN(t.getTime()) ? '' : t.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })
})

function fmtFecha(iso: string): string {
  const x = new Date(iso + 'T00:00:00')
  return isNaN(x.getTime()) ? iso : x.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
}
function urgencia(dias: number): string {
  return dias <= 7 ? 'u-critica' : dias <= 30 ? 'u-alta' : dias <= 90 ? 'u-media' : 'u-info'
}
function urgenciaTxt(dias: number): string {
  return dias <= 7 ? 'txt-rojo' : dias <= 30 ? 'txt-ambar' : 'txt-azul'
}

const ETAPAS: Record<string, { t: string; c: string }> = {
  preparacion: { t: 'Preparación', c: 'p-gris' },
  ejecucion: { t: 'Ejecución', c: 'p-ambar' },
  evaluacion: { t: 'Evaluación', c: 'p-azul' },
  informe: { t: 'Informe', c: 'p-teal' },
  finalizado: { t: 'Finalizado', c: 'p-verde' }
}
const etapaTexto = (e: string) => ETAPAS[e]?.t ?? e
const etapaClase = (e: string) => ETAPAS[e]?.c ?? 'p-gris'

function estadoPlanClase(e: string): string {
  return { NO_INICIADO: 'p-naranja', EN_DESARROLLO: 'p-ambar', CUMPLIDO: 'p-verde', NO_CUMPLIDO: 'p-rojo', ANULADO: 'p-azul' }[e] ?? 'p-gris'
}

function ir(ruta: string): void { router.push(ruta) }

function scrollAlertas(): void {
  refAlertas.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

async function atender(a: AlertaDashboard): Promise<void> {
  const r = await atenderAlertaService(a)
  if (r.ok && d.value) {
    d.value.alertas.items = d.value.alertas.items.filter(x => x !== a)
    d.value.alertas.total = Math.max(0, d.value.alertas.total - 1)
    if (['critica', 'alta'].includes(a.severidad)) {
      d.value.kpis.alertas_criticas = Math.max(0, d.value.kpis.alertas_criticas - 1)
    }
  }
}

async function cargar(): Promise<void> {
  cargando.value = true
  error.value = ''
  const r = await getDashboardService(filtros.idEjecucion)
  if (r.ok && r.data) {
    d.value = r.data
    if (!filtros.idEjecucion && r.data.id_ejecucion_foco) filtros.idEjecucion = r.data.id_ejecucion_foco
  } else {
    error.value = r.error ?? 'No se pudo cargar el dashboard'
  }
  cargando.value = false
}

onMounted(cargar)
watch(() => filtros.idEjecucion, (nuevo, viejo) => { if (nuevo !== viejo && viejo !== null) cargar() })
watch(error, (val) => { if (val) mostrarMensaje(val, 'error') })
</script>

<style scoped>
.dash {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  width: 96%;
  max-width: 1280px;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px auto;
  font-family: var(--font-family);
  min-width: 0;
}
@media (max-width: 640px) {
  .dash { padding: 1rem; }
}

.header-flex { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; flex-wrap: wrap; min-width: 0; }
.header-flex h1 { margin: 0; font-size: 1.4rem; font-weight: 700; color: var(--text-primary); word-break: break-word; overflow-wrap: break-word; }
@media (max-width: 480px) {
  .header-flex h1 { font-size: 1.1rem; }
  .rol-chip { display: block; margin: 0.35rem 0 0; }
}
.subtitulo { font-size: 0.85rem; color: #666; margin: 0.25rem 0 0; text-transform: capitalize; }
.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }
.rol-chip { background: #e8f4fd; color: #0e69a0; border: 1px solid #cfe5f4; padding: 2px 12px; border-radius: var(--radius-pill); font-size: 0.74rem; font-weight: 700; margin-left: 0.6rem; vertical-align: middle; }

.kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 0.8rem; margin-bottom: 1rem; }
@media (max-width: 950px) { .kpis { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 560px) { .kpis { grid-template-columns: 1fr; } }
.kpi {
  text-align: left; background: #fff; border: 1px solid var(--border-separator); border-radius: var(--radius-lg);
  padding: 0.9rem 1rem; box-shadow: var(--shadow-sm); cursor: pointer; display: flex; flex-direction: column; gap: 2px;
  transition: box-shadow 0.15s ease, transform 0.15s ease; font-family: inherit; min-width: 0; overflow: hidden;
}
.kpi:hover { box-shadow: var(--shadow-md); transform: translateY(-1px); }
.kpi-critico { border-color: #dc3545; background: #fff8f8; }
.kpi-alto { border-color: #eea222; background: #fffdf6; }
.kpi-num { font-size: 1.9rem; font-weight: 800; color: #333; line-height: 1.1; }
.kpi-num small { font-size: 0.85rem; font-weight: 600; color: var(--text-muted); }
.kpi-num.azul { color: #0e69a0; }
.kpi-lbl { font-size: 0.82rem; font-weight: 700; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.kpi-sub { font-size: 0.72rem; color: var(--text-muted); }
.kpi-barra { background: #e9eef2; border-radius: 4px; height: 6px; margin-top: 6px; overflow: hidden; }
.kpi-barra-rel { display: block; height: 100%; background: linear-gradient(90deg, #0e69a0, #2b9bd1); border-radius: 4px; }
.txt-rojo { color: #dc3545 !important; }
.txt-ambar { color: #b07708 !important; }
.txt-azul { color: #0e69a0 !important; }
.txt-verde { color: #28a745 !important; }
.txt-fuerte { color: #28a745 !important; }
.txt-debil { color: #b07708 !important; }
.txt-muy_debil { color: #dc3545 !important; }

.grid { display: grid; grid-template-columns: repeat(12, 1fr); gap: 0.9rem; margin-bottom: 0.9rem; min-width: 0; }
.col-7 { grid-column: span 7; min-width: 0; } .col-5 { grid-column: span 5; min-width: 0; }
@media (max-width: 950px) { .col-7, .col-5 { grid-column: span 12; } }

.tarjeta { background: #fff; border: 1px solid var(--border-separator); border-radius: var(--radius-lg); padding: 0.95rem 1.1rem; box-shadow: var(--shadow-sm); margin-bottom: 0.9rem; min-width: 0; overflow: hidden; }
.tarjeta-cab { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem; margin-bottom: 0.7rem; padding-bottom: 0.55rem; border-bottom: 2px solid #e8f1f7; min-width: 0; }
.tarjeta-cab h2 { color: #0e69a0; margin: 0; font-size: 1.02rem; }
.cuenta { font-size: 0.75rem; color: var(--text-disabled); }
.vacio { padding: 1.1rem; color: var(--text-muted); text-align: center; font-size: 0.86rem; }
.vacio a { color: #0e69a0; font-weight: 600; }
.vacio.ok { display: flex; align-items: center; justify-content: center; gap: 0.5rem; color: #155724; }
.ok-ico { width: 26px; height: 26px; background: #d4edda; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 800; }

.resumen-sev { display: flex; gap: 0.3rem; }
/* Se desplazan en vez de recortarse: antes solo se veían 6 y las demás se
   anunciaban con un "mostrando 6 de 10" al que no se podía llegar. */
.alertas {
  list-style: none; margin: 0; padding: 0;
  max-height: 26rem; overflow-y: auto;
  scrollbar-width: thin;
}
.alertas::-webkit-scrollbar { width: 8px; }
.alertas::-webkit-scrollbar-thumb { background: #cfd8e3; border-radius: 999px; }
.alertas::-webkit-scrollbar-thumb:hover { background: #b6c3d2; }
.alerta { display: flex; align-items: flex-start; gap: 0.6rem; padding: 0.55rem 0.5rem; border-left: 3px solid transparent; border-bottom: 1px solid #f2f2f2; border-radius: 4px; }
.b-critica { border-left-color: #dc3545; }
.b-alta { border-left-color: #eea222; }
.b-media { border-left-color: #0e69a0; }
.b-informativa { border-left-color: #c9c9c9; }
.sev-ico { flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.76rem; font-weight: 800; margin-top: 2px; }
.s-critica { background: #f8d7da; color: #721c24; }
.s-alta { background: #fff3cd; color: #856404; }
.s-media { background: #e8f4fd; color: #0e69a0; }
.s-informativa { background: #f0f0f0; color: #666; }
.alerta-cuerpo { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.alerta-cuerpo.enlazable { cursor: pointer; }
.alerta-cuerpo.enlazable:hover .alerta-titulo { color: #0e69a0; text-decoration: underline; }
.alerta-titulo { font-size: 0.84rem; font-weight: 600; color: #333; }
.alerta-detalle { font-size: 0.75rem; color: var(--text-tertiary); margin-top: 1px; }
.btn-atender { flex-shrink: 0; border: 1px solid #c8e6c9; background: #f4fbf4; color: #2e7d32; width: 26px; height: 26px; border-radius: 50%; cursor: pointer; font-weight: 800; }
.btn-atender:hover { background: #28a745; color: #fff; }
.ver-mas { font-size: 0.74rem; color: var(--text-muted); margin: 0.5rem 0 0; text-align: center; }

.eventos { list-style: none; margin: 0; padding: 0; }
.evento { display: flex; align-items: center; gap: 0.7rem; padding: 0.5rem 0.4rem; border-bottom: 1px solid #f2f2f2; cursor: pointer; border-radius: 6px; }
.evento:hover { background: #f5f9fc; }
.ev-dias { min-width: 46px; text-align: center; font-weight: 800; font-size: 1.05rem; border-radius: var(--radius-md); padding: 0.3rem 0.2rem; }
.ev-dias small { font-size: 0.62rem; font-weight: 600; }
.u-critica { background: #f8d7da; color: #721c24; }
.u-alta { background: #fff3cd; color: #856404; }
.u-media { background: #e8f4fd; color: #0e69a0; }
.u-info { background: #f0f0f0; color: #666; }
.ev-cuerpo { display: flex; flex-direction: column; min-width: 0; }
.ev-titulo { font-size: 0.84rem; font-weight: 600; color: #333; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ev-meta { font-size: 0.72rem; color: var(--text-muted); }

.hoy-toca { border-left: 4px solid #eea222; }
.toca-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 0.6rem; }
.toca-item { text-align: left; background: #fffdf6; border: 1px solid #f3e3c0; border-radius: var(--radius-md); padding: 0.7rem 0.85rem; cursor: pointer; display: flex; flex-direction: column; gap: 2px; font-family: inherit; }
.toca-item:hover { border-color: #eea222; box-shadow: var(--shadow-sm); }
.toca-factor { font-weight: 700; color: #333; font-size: 0.88rem; }
.toca-meta { font-size: 0.72rem; color: var(--text-muted); }
.toca-datos { font-size: 0.78rem; color: var(--text-secondary); margin-top: 4px; }
.toca-plan { margin: 0.7rem 0 0; font-size: 0.8rem; color: #856404; background: #fff3cd; border-radius: var(--radius-md); padding: 0.5rem 0.8rem; }

.contenedor-tabla { overflow-x: auto; }
.lista-tabla { width: 100%; border-collapse: collapse; font-size: 0.83rem; }
.lista-tabla th { background: #0e69a0; color: #fff; padding: 0.5rem 0.6rem; text-align: left; text-transform: uppercase; font-size: 0.68rem; letter-spacing: 0.03em; border: 1px solid #0b587f; }
.lista-tabla td { border: 1px solid var(--border-separator); padding: 0.5rem 0.6rem; vertical-align: middle; }
.fila-prog { cursor: pointer; }
.fila-prog:hover td { background: #f5f9fc; }
.prog-nombre { font-weight: 600; }
.ctr { text-align: center; }
.mini-barra { background: #e9eef2; border-radius: 4px; height: 7px; width: 90px; max-width: 100%; margin: 0 auto 2px; overflow: hidden; }
.mini-barra span { display: block; height: 100%; background: linear-gradient(90deg, #0e69a0, #2b9bd1); }
.sin-dato { color: var(--text-disabled); font-size: 0.76rem; }
.flecha { color: #0e69a0; font-weight: 800; }
.semaforo-paginacion { display: flex; align-items: center; justify-content: flex-end; gap: 0.6rem; padding: 0.6rem 0 0.1rem; }
.pag-btn { border: 1px solid #0e69a0; background: #fff; color: #0e69a0; padding: 0.3rem 0.85rem; border-radius: 6px; font-size: 0.82rem; font-weight: 600; cursor: pointer; transition: all 0.15s ease; }
.pag-btn:hover:not(:disabled) { background: #0e69a0; color: #fff; }
.pag-btn:disabled { opacity: 0.4; cursor: default; }
.pag-info { font-size: 0.8rem; color: #666; }

.pill { display: inline-block; padding: 1px 9px; border-radius: var(--radius-pill); font-size: 0.7rem; font-weight: 700; white-space: nowrap; }
.p-verde { background: #d4edda; color: #155724; }
.p-ambar { background: #fff3cd; color: #856404; }
.p-rojo { background: #f8d7da; color: #721c24; }
.p-azul { background: #e8f4fd; color: #0e69a0; }
.p-teal { background: #d1ecf1; color: #0c5460; }
.p-gris { background: #f0f0f0; color: #666; }
.p-naranja { background: #ffe6c4; color: #7a4a08; }

.plan-kpis { display: flex; gap: 1.4rem; margin-bottom: 0.7rem; }
.plan-kpi { display: flex; flex-direction: column; }
.pk-num { font-size: 1.5rem; font-weight: 800; color: #333; }
.pk-num.azul { color: #0e69a0; }
.pk-lbl { font-size: 0.72rem; color: var(--text-muted); }
.plan-estados { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-bottom: 0.7rem; }

/* Procesos por tipo */
.tipos-grid { display: flex; flex-wrap: wrap; gap: 1.8rem; }
.tipo-kpi { display: flex; flex-direction: column; min-width: 7rem; }
.tk-num { font-size: 1.7rem; font-weight: 800; color: #0e69a0; line-height: 1.1; }
.tk-lbl { font-size: 0.74rem; color: var(--text-muted); }
.tipo-kpi.sin-tipificar .tk-num { color: #b06000; }
.enlace-plan { align-self: flex-start; }

.selector { font-size: 0.8rem; color: var(--text-secondary); font-weight: 600; display: inline-flex; align-items: center; flex-wrap: wrap; gap: 0.25rem; max-width: 100%; min-width: 0; }
.selector select { margin-left: 0.2rem; padding: 0.3rem 0.5rem; border: 1px solid var(--border-medium); border-radius: var(--radius-sm); font-weight: 400; max-width: 260px; min-width: 0; flex-shrink: 1; }
@media (max-width: 860px) {
  .tarjeta-cab { flex-direction: column; align-items: flex-start; }
  .selector { width: 100%; }
  .selector select { max-width: 100%; width: 100%; margin-left: 0; }
}

/* Botones */
.btn { padding: 0.5rem 1rem; font-weight: 600; border: none; border-radius: 8px; cursor: pointer; transition: all 0.15s ease; font-family: var(--font-family); font-size: 0.85rem; }
.btn.secundario { background: white; border: 1px solid #0e69a0; color: #0e69a0; }
.btn.secundario:hover { background: #0e69a0; color: #fff; }
.btn.chico { background: white; border: 1px solid #0e69a0; color: #0e69a0; padding: 0.3rem 0.75rem; font-size: 0.78rem; }
.btn.chico:hover { background: #0e69a0; color: #fff; }

.aviso-error { background: var(--color-error-bg); color: var(--color-error-text); padding: 0.8rem 1rem; border-radius: var(--radius-md); display: flex; align-items: center; gap: 0.8rem; }
.pie { text-align: right; color: var(--text-disabled); font-size: 0.72rem; margin: 0.2rem 0 0; }

/* Skeletons */
.skeleton { background: linear-gradient(90deg, #eef1f4 25%, #f7f9fb 50%, #eef1f4 75%); background-size: 200% 100%; animation: brillo 1.2s infinite; border-radius: var(--radius-lg); }
.sk-kpi { height: 110px; }
.sk-tarjeta { height: 300px; }
@keyframes brillo { from { background-position: 200% 0; } to { background-position: -200% 0; } }
</style>
